export interface RenderPipelineContext<I, O> {
  input: I
  output: O
}

/**
 * Marks a context whose output has not been produced yet — createContext was
 * given no initial output and no setup step has run. It is a distinct "absent"
 * type (not `{}`, which behaves like `any`), and MergeOutput drops it so the
 * first real output type — from an initial output or the first setup — wins.
 */
export type EmptyOutput = Record<string, never>

/**
 * Adds a produced `Output` slice onto prior output, treating EmptyOutput as
 * absent so only real output types are carried forward in the built-up union.
 */
export type MergeOutput<Prior, Output> = [Prior] extends [EmptyOutput]
  ? Output
  : Prior & Output

/**
 * Pipeline step: merges `Output` onto context.output. Return type of feature setup().
 *
 * `InitialOutput` constrains the output already accumulated on the context at
 * runtime — the incoming `O` must satisfy it, so a step can require slices
 * produced by earlier steps.
 */
export type FeatureSetup<Input, Output, InitialOutput = unknown> = <
  I extends Input,
  O extends InitialOutput,
>(
  context: RenderPipelineContext<I, O>,
) => RenderPipelineContext<I, MergeOutput<O, Output>>

/**
 * Lifts an input (and optional starting output) into a pipeline context
 * `{ input, output }` for feature setup steps to build on. With no initial
 * output the output is typed EmptyOutput — "not produced yet" — until the
 * first setup step resolves the real first output type.
 */
const createContext =
  <I, O extends Record<string, unknown> = EmptyOutput>(
    initialOutput: O = {} as O,
  ) =>
  (input: I): RenderPipelineContext<I, O> => ({
    input,
    output: initialOutput,
  })

type PipelineOutput<Context> =
  Context extends RenderPipelineContext<unknown, infer O> ? O : Context

const getOutput =
  () =>
  <Context extends RenderPipelineContext<unknown, unknown>>(
    context: Context,
  ): PipelineOutput<Context> =>
    (context as RenderPipelineContext<unknown, unknown>)
      .output as PipelineOutput<Context>

type AnyContext = RenderPipelineContext<object, unknown>

/**
 * Merges `fn(input, ctx)` onto `input` and keeps the incoming context type.
 * `createFeatureSetup` restores the original input, so the widened runtime
 * input does not need to leak into the step's type.
 */
const updateInput =
  (fn: (input: any, context: any) => any) =>
  <T extends AnyContext>(context: T): T =>
    ({
      ...context,
      input: {
        ...(context.input as object),
        ...fn(context.input, context),
      },
    }) as T

/**
 * Merges `fn(output, ctx)` onto `output` and keeps the incoming context type.
 * `createFeatureSetup` re-declares the produced slice via its Output generic.
 */
const updateOutput =
  (fn: (output: any, context: any) => any) =>
  <T extends AnyContext>(context: T): T =>
    ({
      ...context,
      output: {
        ...(context.output as object),
        ...fn(context.output, context),
      },
    }) as T

/**
 * Builds a FeatureSetup from a ctx-step. Only `output` from `fn` is kept;
 * the original `input` is restored so per-setup input edits never leak.
 */
const createFeatureSetup = <Input, Output, InitialOutput = unknown>(
  fn: (context: RenderPipelineContext<Input, InitialOutput>) => unknown,
): FeatureSetup<Input, Output, InitialOutput> =>
  ((context: RenderPipelineContext<Input, InitialOutput>) => {
    const next = fn(context) as { output?: object }
    return {
      input: context.input,
      output: { ...context.output, ...next.output } as MergeOutput<
        InitialOutput,
        Output
      >,
    }
  }) as unknown as FeatureSetup<Input, Output, InitialOutput>

export const RenderPipeline = {
  createContext,
  createFeatureSetup,
  updateInput,
  updateOutput,
  getOutput,
}
